import React, { MouseEvent, useState } from "react";
import {
  Box,
  IconButton,
  Popper,
  Grow,
  Paper,
  ClickAwayListener,
  Button,
  PopperPlacementType
} from "@mui/material";
import PopUpContent from "./PopUpContent";
import { IntraOralValue } from "../../../interfaces/AssessmentInterfaces";

export const numbers = ["1", "2", "3", "4", "5", "6", "7", "8"];
export const alphabets = ["A", "B", "C", "D", "E"];

interface IntraOralContentProps {
  themeColor: string;
  numbering: "normal" | "alphabets";
  type: "permanent" | "primary";
  stateValue: Record<"permanent" | "primary", Record<string, IntraOralValue>>;
  onNodeDelete: (string: string | Array<string>) => void;
  onNumberSave: (comboString: string | Array<string>, data: IntraOralValue) => void;
}

function IntraOralContent(props: IntraOralContentProps): React.JSX.Element {
  const { themeColor, numbering, type, stateValue, onNumberSave, onNodeDelete } = props;

  const color = themeColor || "#009654";

  const [open, setOpen] = React.useState(false);
  const [quarterIndex, setQuarterIndex] = useState(numbers);
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [placement, setPlacement] = React.useState<PopperPlacementType>();
  const [popUpValues, setPopUpValues] = useState<{
    quarter: string;
    quarterNum: string;
  }>({ quarter: "", quarterNum: "" });

  React.useEffect(() => {
    if (numbering === "alphabets") {
      setQuarterIndex(alphabets);
    } else {
      setQuarterIndex(numbers);
    }
  }, [numbering]);

  const isIconSelected = (quarterAlpha: string, quarterNum: string, numVal: string) => {
    let key;
    if (numbering === "alphabets") {
      key = quarterAlpha;
    } else {
      key = quarterNum;
    }
    key += numVal;
    if (key in stateValue[type]) return true;
    return false;
  };

  const isIconActive = (quarterAlpha: string, quarterNum: string, numVal: string) => {
    if (
      (popUpValues.quarter === quarterAlpha || popUpValues.quarter === quarterNum) &&
      popUpValues.quarterNum === numVal &&
      open
    )
      return true;
    return false;
  };

  const handleClosePopup = () => {
    setOpen(false);
    setSelectAll(false);
  };

  const handleClick = (newPlacement: PopperPlacementType, event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen((prev) => placement !== newPlacement || !prev);
    setPlacement(newPlacement);
  };

  const handleSelectAll = ({ event }: { event: MouseEvent<HTMLButtonElement> }) => {
    setSelectAll(true);
    setPopUpValues({ quarter: "", quarterNum: "" });
    handleClick("bottom-start", event);
  };

  const handleUnselectAll = () => {
    setSelectAll(false);
    setPopUpValues({ quarter: "", quarterNum: "" });
    const permanentComboStrings: Array<string> = numbers
      .slice(0, 4)
      .flatMap((num) => numbers.map((section) => `${num}${section}`));

    const primaryComboStrings: Array<string> = numbers
      .slice(4)
      .flatMap((num) => alphabets.map((section) => `${num}${section}`));

    if (type === "permanent") onNodeDelete(permanentComboStrings);
    if (type === "primary") onNodeDelete(primaryComboStrings);
  };

  const getIconButtonSx = (quarterAlpha: string, quarterNum: string, numVal: string) => ({
    height: "32px",
    width: "32px",
    padding: 0,
    margin: "0px 4px",
    ...(isIconSelected(quarterAlpha, quarterNum, numVal) && {
      backgroundColor: color,
      color: "white",
      "&:hover": { opacity: 0.5, backgroundColor: color }
    }),
    ...(isIconActive(quarterAlpha, quarterNum, numVal) && {
      border: `3px solid ${color}`
    })
  });

  return (
    <>
      <Popper
        sx={{ zIndex: (theme) => theme.zIndex.tooltip }}
        open={open}
        anchorEl={anchorEl}
        placement={placement}
        transition
      >
        {({ TransitionProps, placement: p }) => (
          <Grow
            {...TransitionProps}
            style={{ transformOrigin: p === "bottom" ? "center top" : "center top" }}
          >
            <Paper variant="outlined">
              <ClickAwayListener onClickAway={() => handleClosePopup()}>
                <Box height="380px" width="380px">
                  <PopUpContent
                    onNumberSave={onNumberSave}
                    stateValue={stateValue}
                    onNodeDelete={onNodeDelete}
                    type={type}
                    quarter={popUpValues.quarter}
                    quarterNum={popUpValues.quarterNum}
                    closePopUp={() => handleClosePopup()}
                    selectAll={selectAll}
                  />
                </Box>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>

      <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            size="small"
            sx={{ fontSize: 12 }}
            variant="outlined"
            onClick={(event: MouseEvent<HTMLButtonElement>) => handleSelectAll({ event })}
          >
            Select all
          </Button>
          <Button size="small" sx={{ fontSize: 12 }} variant="outlined" onClick={handleUnselectAll}>
            Unselect All
          </Button>
        </Box>

        <Box sx={{ display: "flex" }}>
          <Box sx={{ border: "1px solid black", borderRight: "none" }}>
            <Box
              sx={{
                height: "50px",
                pr: 2,
                display: "flex",
                alignItems: "center",
                borderBottom: "1px solid black"
              }}
            >
              {quarterIndex
                .slice(0)
                .reverse()
                .map((q) => (
                  <IconButton
                    data-testmation="intraOralIconButton"
                    key={q}
                    size="medium"
                    sx={getIconButtonSx("5", "1", q)}
                    aria-controls={open ? "menu-list-grow" : undefined}
                    aria-haspopup="true"
                    onClick={(e: MouseEvent<HTMLButtonElement>) => {
                      setPopUpValues({
                        quarter: numbering === "alphabets" ? "5" : "1",
                        quarterNum: q
                      });
                      handleClick("bottom-start", e);
                    }}
                  >
                    {q}
                  </IconButton>
                ))}
            </Box>
            <Box sx={{ height: "50px", pr: 2, display: "flex", alignItems: "center" }}>
              {quarterIndex
                .slice(0)
                .reverse()
                .map((q) => (
                  <IconButton
                    data-testmation="intraOralIconButton"
                    key={q}
                    size="medium"
                    sx={getIconButtonSx("8", "4", q)}
                    aria-controls={open ? "menu-list-grow" : undefined}
                    aria-haspopup="true"
                    onClick={(e) => {
                      setPopUpValues({
                        quarter: numbering === "alphabets" ? "8" : "4",
                        quarterNum: q
                      });
                      handleClick("bottom-start", e);
                    }}
                  >
                    {q}
                  </IconButton>
                ))}
            </Box>
          </Box>

          <Box sx={{ border: "1px solid black" }}>
            <Box
              sx={{
                height: "50px",
                pl: 2,
                display: "flex",
                alignItems: "center",
                borderBottom: "1px solid black"
              }}
            >
              {quarterIndex.map((q) => (
                <IconButton
                  data-testmation="intraOralIconButton"
                  key={q}
                  size="medium"
                  sx={getIconButtonSx("6", "2", q)}
                  aria-controls={open ? "menu-list-grow" : undefined}
                  aria-haspopup="true"
                  onClick={(e) => {
                    setPopUpValues({
                      quarter: numbering === "alphabets" ? "6" : "2",
                      quarterNum: q
                    });
                    handleClick("bottom-start", e);
                  }}
                >
                  {q}
                </IconButton>
              ))}
            </Box>
            <Box sx={{ height: "50px", pl: 2, display: "flex", alignItems: "center" }}>
              {quarterIndex.map((q) => (
                <IconButton
                  data-testmation="intraOralIconButton"
                  key={q}
                  size="medium"
                  sx={getIconButtonSx("7", "3", q)}
                  aria-controls={open ? "menu-list-grow" : undefined}
                  aria-haspopup="true"
                  onClick={(e) => {
                    setPopUpValues({
                      quarter: numbering === "alphabets" ? "7" : "3",
                      quarterNum: q
                    });
                    handleClick("bottom-start", e);
                  }}
                >
                  {q}
                </IconButton>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}

export default IntraOralContent;
